import { log } from '@/log'
import { localStorageKeys } from '@/registry/local-storage-keys'
import { windowEventNames } from '@/registry/window-event-names'

export type LocalNotificationLevel = 'caution' | 'alert'

export type LocalNotificationKind = 'auth-invalidated'

export interface LocalNotificationButton {
  label: string
  path: string
}

export interface LocalNotification {
  id: string
  kind: LocalNotificationKind
  level: LocalNotificationLevel
  title: string
  text?: string
  button?: LocalNotificationButton
  createdAt: number
}

export interface LocalNotificationInput {
  id?: string
  kind: LocalNotificationKind
  level?: LocalNotificationLevel
  title: string
  text?: string
  button?: LocalNotificationButton
}

interface LocalNotificationStorageDependency {
  storage: Storage
  now: () => number
  createId: () => string
  eventTarget?: EventTarget
}

const STORAGE_KEY = localStorageKeys.localNotifications
const STORAGE_CHANGED_EVENT_NAME = windowEventNames.localNotificationStorageChanged
const ID_RADIX = 36
const ID_RANDOM_PREFIX_LENGTH = 2

const defaultDependency = (): LocalNotificationStorageDependency => ({
  storage: localStorage,
  now: () => Date.now(),
  createId: () => `${Date.now()}-${Math.random().toString(ID_RADIX).slice(ID_RANDOM_PREFIX_LENGTH)}`,
})

const isObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null

const isString = (value: unknown): value is string =>
  typeof value === 'string'

const isLocalNotificationLevel = (value: unknown): value is LocalNotificationLevel =>
  value === 'caution' || value === 'alert'

const isLocalNotificationKind = (value: unknown): value is LocalNotificationKind =>
  value === 'auth-invalidated'

const isButton = (value: unknown): value is LocalNotificationButton =>
  isObject(value) && isString(value.label) && isString(value.path)

const toNotification = (value: unknown): LocalNotification | null => {
  try {
    if (!isObject(value)) throw new Error('not an object')
    if (!isString(value.id)) throw new Error('id is not a string')
    if (!isLocalNotificationKind(value.kind)) throw new Error('unknown kind')
    if (!isLocalNotificationLevel(value.level)) throw new Error('unknown level')
    if (!isString(value.title)) throw new Error('title is not a string')
    if (value.text !== undefined && !isString(value.text)) throw new Error('text is not a string')
    if (typeof value.createdAt !== 'number') throw new Error('createdAt is not a number')

    const maybeButton = value.button

    return {
      id: value.id,
      kind: value.kind,
      level: value.level,
      title: value.title,
      ...(isString(value.text) ? { text: value.text } : {}),
      ...(isButton(maybeButton) ? { button: maybeButton } : {}),
      createdAt: value.createdAt,
    }
  } catch (e) {
    log.error('LocalNotification: skipping item:', e, value)

    return null
  }
}

export class LocalNotificationStorage {
  static readonly storageKey = STORAGE_KEY
  static readonly storageChangedEventName = STORAGE_CHANGED_EVENT_NAME

  static createDefault(): LocalNotificationStorage {
    return new LocalNotificationStorage()
  }

  constructor(
    private readonly dependency: LocalNotificationStorageDependency = defaultDependency()
  ) {}

  list(limit?: number): LocalNotification[] {
    const raw = this.readValue()
    if (raw === null) {
      return []
    }

    try {
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) {
        throw new Error(`stored value is not an array: ${typeof parsed}`)
      }

      const notifications = parsed
        .map(toNotification)
        .filter((notification): notification is LocalNotification => notification !== null)

      return limit !== undefined ? notifications.slice(0, limit) : notifications
    } catch (e) {
      log.error(e)

      return []
    }
  }

  add(input: LocalNotificationInput): LocalNotification {
    const notification: LocalNotification = {
      id: input.id || this.dependency.createId(),
      kind: input.kind,
      level: input.level || 'caution',
      title: input.title,
      ...(input.text ? { text: input.text } : {}),
      ...(input.button ? { button: input.button } : {}),
      createdAt: this.dependency.now(),
    }

    const others = this.list().filter(current => current.id !== notification.id)
    this.overwrite([
      ...others,
      notification,
    ])

    return notification
  }

  remove(id: string): void {
    this.overwrite(this.list().filter(notification => notification.id !== id))
  }

  removeWhere(predicate: (notification: LocalNotification) => boolean): void {
    this.overwrite(this.list().filter(notification => !predicate(notification)))
  }

  clearByKind(kind: LocalNotificationKind): void {
    this.removeWhere(notification => notification.kind === kind)
  }

  clearAll(): void {
    this.removeValue()
  }

  subscribe(listener: () => void): () => void {
    const eventTarget = this.eventTarget()
    if (eventTarget === null) {
      return () => undefined
    }

    eventTarget.addEventListener(STORAGE_CHANGED_EVENT_NAME, listener)

    return () => {
      eventTarget.removeEventListener(STORAGE_CHANGED_EVENT_NAME, listener)
    }
  }

  private overwrite(notifications: LocalNotification[]): void {
    this.writeValue(JSON.stringify(notifications))
    this.dispatchStorageChanged()
  }

  private readValue(): string | null {
    try {
      return this.dependency.storage.getItem(STORAGE_KEY)
    } catch (e) {
      // localStorage が利用できない場合でも例外は投げず処理を継続する
      log.error(e)

      return null
    }
  }

  private writeValue(value: string): void {
    try {
      this.dependency.storage.setItem(STORAGE_KEY, value)
    } catch (e) {
      // localStorage が利用できない場合でも例外は投げず処理を継続する
      log.error(e)
    }
  }

  private removeValue(): void {
    try {
      this.dependency.storage.removeItem(STORAGE_KEY)
      this.dispatchStorageChanged()
    } catch (e) {
      // localStorage が利用できない場合でも例外は投げず処理を継続する
      log.error(e)
    }
  }

  private eventTarget(): EventTarget | null {
    if (this.dependency.eventTarget) {
      return this.dependency.eventTarget
    }

    if (typeof window === 'undefined') {
      return null
    }

    return window
  }

  private dispatchStorageChanged(): void {
    const eventTarget = this.eventTarget()
    if (eventTarget === null || typeof Event !== 'function') {
      return
    }

    eventTarget.dispatchEvent(new Event(STORAGE_CHANGED_EVENT_NAME))
  }
}

export const localNotificationStorage = LocalNotificationStorage.createDefault()
